import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { ThemeProvider } from "./i18n/ThemeContext";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import Services from "./components/Services";
import About from "./components/About";
import Clients from "./components/Clients";
import CaseStudies from "./components/CaseStudies";
import AITransformation from "./components/AITransformation";
import DeepMeta from "./components/DeepMeta";
import Methodology from "./components/Methodology";
import Testimonials from "./components/Testimonials";
import VideoSection from "./components/VideoSection";
import Blog from "./components/Blog";
import BlogArticle from "./components/BlogArticle";
import CookieConsent from "./components/CookieConsent";
import AdminDashboard from "./components/AdminDashboard";
import NotFound from "./components/NotFound";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfUse from "./components/TermsOfUse";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppButton from "./components/WhatsAppButton";
import FloatingCTA from "./components/FloatingCTA";
import BlogPreview from "./components/BlogPreview";

import Contact from "./components/Contact";
import Footer from "./components/Footer";
import SectionDivider from "./components/SectionDivider";

const Home = () => {
  const { lang, t } = useLanguage();

  useEffect(() => {
    document.title = t.meta.title;
    document.documentElement.lang = lang;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", t.meta.description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", t.meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", t.meta.description);
  }, [t, lang]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, []);

  return (
    <main className="bg-[#0F1A2E] min-h-screen">
      <a href="#services" className="skip-link">Skip to content</a>
      <Navbar />
      <Hero />
      <VideoSection />
      <Stats />
      <SectionDivider />
      <Services />
      <Methodology />
      <SectionDivider />
      <CaseStudies />
      <Clients />
      <SectionDivider />
      <Testimonials />
      <About />
      <SectionDivider />
      <AITransformation />
      <DeepMeta />
      <BlogPreview />
      <Contact />
      <Footer />
    </main>
  );
};

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-enter">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

function App() {
  useEffect(() => {
    const preloader = document.getElementById("preloader");
    if (preloader) {
      const timer = setTimeout(() => {
        preloader.style.opacity = "0";
        setTimeout(() => preloader.remove(), 500);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="App">
      <ThemeProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AnimatedRoutes />
          <CookieConsent />
          <ScrollToTop />
          <WhatsAppButton />
          <FloatingCTA />
        </BrowserRouter>
      </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
